import format from 'date-fns/format';
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  selectSortedWhitelistFormRecentDownloads,
  selectSortedWhitelistFormClaimantsList,
} from '../selectors/WhitelistRequestFormSelectors';

// In safari and iPhone browsers, the string format is not a compatible date ISO format.
// Converting the empty space between the date and time to a `T` converts the string to an ISO compatible format
const getIsoDateFromString = (date) => {
  return new Date(date.replace(' ', 'T'));
};

const WhitelistRequestForm = ({
  errorMessage,
  isLoading,
  submitClaimDispute,
  validateYouTubeChannel,
  youTubeChannelValidationError,
}: {
  errorMessage: string;
  isLoading: boolean;
  submitClaimDispute: (request: object) => void;
  validateYouTubeChannel: (channelId: string) => void;
  youTubeChannelValidationError: string;
}) => {
  const recentDownloads = useSelector(selectSortedWhitelistFormRecentDownloads);
  const claimantsList = useSelector(selectSortedWhitelistFormClaimantsList);

  const [audioTrackId, setAudioTrackId] = useState('');
  const [audioTrackUrl, setAudioTrackUrl] = useState('');
  const [claimant, setClaimant] = useState('');
  const [otherClaimant, setOtherClaimant] = useState('');
  const [youtubeChannelId, setYoutubeChannelId] = useState('');
  const [youtubeVideoUrl, setYoutubeVideoUrl] = useState('');

  useEffect(() => {
    if (audioTrackId !== '') {
      setAudioTrackUrl('');
    }
  }, [audioTrackId]);

  useEffect(() => {
    if (audioTrackUrl !== '') {
      setAudioTrackId('');
    }
  }, [audioTrackUrl]);

  const handleSubmitForm = () => {
    submitClaimDispute({
      audioTrackId,
      audioTrackUrl,
      claimant,
      otherClaimant,
      youtubeChannelId,
      youtubeVideoUrl,
    });
  };

  const getClaimantOptions = () => (
    <>
      {claimantsList.map((claimant) =>
        claimant.toLowerCase() === 'other' ? null : ( // Hard code Other at the end of the list
          <option key={claimant} value={claimant}>
            {claimant}
          </option>
        )
      )}
    </>
  );

  const renderClaimantDropDown = () => (
    <div className="whitelistRequestForm-inputBlock">
      <h5 className="whitelistRequestForm-inputBlockLabel">Claimant</h5>

      <select
        className="whitelistRequestForm-selectClaimant"
        onChange={({ target }) => setClaimant(target.value)}
        value={claimant}
      >
        <option key="select" disabled value="">
          {' '}
          -- select a claimant --{' '}
        </option>
        {getClaimantOptions()}
        <option key="Other" value="Other">
          Other
        </option>
      </select>

      {claimant === 'Other' && (
        <p className="whitelistRequestForm-other">
          <input
            type="text"
            name="claimantOther"
            className="whitelistRequestForm-textField"
            placeholder="Claimant Name"
            onChange={({ target }) => setOtherClaimant(target.value)}
            value={otherClaimant}
          />
        </p>
      )}

      <p className="whitelistRequestForm-claimantExplanation">
        The company that is claiming to hold the copyright to the audio.
        <br />
        If the claimant is not listed here, please{' '}
        <a
          href="https://support.google.com/youtube/answer/2797454"
          rel="noopener noreferrer"
          target="_blank"
        >
          file a dispute directly through YouTube
        </a>
        .
      </p>
    </div>
  );

  const renderYouTubeFields = () => (
    <>
      <div className="whitelistRequestForm-inputBlock">
        <h5 className="whitelistRequestForm-inputBlockLabel">
          YouTube Channel ID
        </h5>

        <input
          type="text"
          name="youtubeChannelId"
          className="whitelistRequestForm-textField"
          placeholder="ex: WJLOW9fg9queUHP471uEOxg"
          onBlur={() => validateYouTubeChannel(youtubeChannelId)}
          onChange={({ target }) => setYoutubeChannelId(target.value)}
        />

        {youTubeChannelValidationError?.length > 0 && (
          <p className="whitelistRequestForm-channel whitelistRequestForm-errorMessage">
            {youTubeChannelValidationError}
          </p>
        )}

        <p className="whitelistRequestForm-channel">
          <a
            href="https://support.google.com/youtube/answer/3250431?hl=en"
            rel="noopener noreferrer"
            target="_blank"
          >
            Where do I find my YouTube channel ID?
          </a>
        </p>
      </div>

      <div className="whitelistRequestForm-inputBlock">
        <h5 className="whitelistRequestForm-inputBlockLabel">
          YouTube Video URL
        </h5>

        <input
          type="text"
          name="youtubeVideoUrl"
          className="whitelistRequestForm-textField"
          placeholder="https://www.youtube.com/watch?..."
          onChange={({ target }) => setYoutubeVideoUrl(target.value)}
        />
      </div>
    </>
  );

  const renderAudioTrackFields = () => (
    <div className="whitelistRequestForm-inputBlock whitelistRequestForm-trackSelection">
      <div>
        <h5 className="whitelistRequestForm-inputBlockLabel">
          Storyblocks Track URL
        </h5>

        <input
          type="text"
          name="audioTrackUrl"
          className="whitelistRequestForm-textField"
          placeholder="https://www.storyblocks.com/audio/stock/..."
          onChange={({ target }) => setAudioTrackUrl(target.value)}
          value={audioTrackUrl}
        />

        <p className="whitelistRequestForm-myDownloadsLink">
          <a href="/member/download-history">My Downloads</a>
        </p>
      </div>

      {recentDownloads.length > 0 && (
        <>
          <div className="whitelistRequestForm-trackSelection-divider">OR</div>

          <div>
            <h5 className="whitelistRequestForm-inputBlockLabel">
              Select the track from your recent downloads
            </h5>
            <select
              className="whitelistRequestForm-selectRecentlyDownloaded"
              name="audioTrackId"
              onChange={({ target }) => setAudioTrackId(target.value)}
              value={audioTrackId}
            >
              <option key="select"> -- select a track -- </option>

              {recentDownloads.map((stockItem) => {
                const { id, title, dateLastAccessed } = stockItem;

                return (
                  <option key={id} value={id}>
                    {title} (
                    {format(getIsoDateFromString(dateLastAccessed), 'MM/d/yy')})
                  </option>
                );
              })}
            </select>
            <p className="whitelistRequestForm-myDownloadsLink">
              The title of the track listed on the claim form may not match
              exactly.
            </p>
          </div>
        </>
      )}
    </div>
  );

  return (
    <form className="whitelistRequestForm-form">
      <h4 className="whitelistRequestForm-formTitle">
        Complete this form to submit your dispute
      </h4>

      <p className="whitelistRequestForm-instructions">
        <i>All fields are required</i>
      </p>

      {errorMessage?.length > 0 && (
        <p className="whitelistRequestForm-errorMessage">{errorMessage}</p>
      )}

      {renderClaimantDropDown()}

      {renderYouTubeFields()}

      {renderAudioTrackFields()}

      <button
        type="button"
        disabled={isLoading}
        className="whitelistRequestForm-submitClaim btn btn-primary"
        onClick={handleSubmitForm}
      >
        Submit claim dispute
      </button>
    </form>
  );
};

export default WhitelistRequestForm;
